import { AnimatePresence, motion } from 'motion/react';
import { useCallback, useState } from 'react';

import { ScanlineOverlay } from '@components/ScanlineOverlay';
import { useBottomPanel } from '@contexts/BottomPanelContext';
import { useOrderNotifications } from '@hooks/useOrderNotifications';
import { type AggregatedPosition } from '@hooks/usePositionAggregation';
import { useRoundTimer } from '@hooks/useRoundTimer';
import { orderService } from '@services/orderService';
import type { Order } from '@services/orderTypes';
import { RoundService } from '@services/roundService';

import { formatPrice } from '../../utils/chartHelpers';
import { Header } from '../shared/Header';

import { ClosePositionPanel } from './ClosePositionModal';
import { ManageOrdersPanel } from './ManageOrdersPanel';
import { TradePanel } from './TradePanel';
import { MarketScreenProps } from './types';
import { useMarketData } from './useMarketData';
import { VisxChart } from './VisxChart';

export function MarketScreen({
  asset,
  advancedMode: _advancedMode,
  onNavigate,
  isRecovering = false,
}: MarketScreenProps) {
  // Use centralized market data hook
  const marketData = useMarketData(asset);

  // Use bottom panel infrastructure
  const {
    state: bottomPanelState,
    openTradePanel,
    openClosePositionPanel,
    openManageOrdersPanel,
    closePanel,
  } = useBottomPanel();

  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);

  // Listen for order events and show notifications
  useOrderNotifications();

  // Get live round timing information
  const { formatTime } = useRoundTimer();

  const handleTradeZoneSelect = useCallback(
    (tradeType: 'CALL' | 'PUT') => {
      // Only allow trade selection when market data is ready
      if (marketData.isReady) {
        openTradePanel({
          tradeType,
          strikePrice: marketData.selectedStrikePrice,
          spotPrice: marketData.spotPrice,
          asset,
        });
      }
    },
    [
      marketData.isReady,
      marketData.selectedStrikePrice,
      marketData.spotPrice,
      asset,
      openTradePanel,
    ],
  );

  const handleTradeZoneDeselect = useCallback(() => {
    closePanel();
  }, [closePanel]);

  const handlePositionSelect = useCallback(
    (position: AggregatedPosition | null) => {
      if (position) {
        openClosePositionPanel({ positionGroup: position });
      } else {
        closePanel();
      }
    },
    [openClosePositionPanel, closePanel],
  );

  const handleOrderSelect = useCallback(
    (order: Order | null) => {
      setSelectedOrder(order);
      // If an order is selected, close any open panels to avoid conflicts
      if (order) {
        closePanel();
      }
    },
    [closePanel],
  );

  const handleManageOrders = useCallback(
    (orders: Order[], strikePrice: number) => {
      // Check if we're already showing this panel - if so, close it (toggle behavior)
      if (
        bottomPanelState.activePanel === 'manageOrders' &&
        bottomPanelState.manageOrdersData?.strikePrice === strikePrice
      ) {
        closePanel();
      } else {
        openManageOrdersPanel({
          orders,
          strikePrice,
          asset,
        });
      }
    },
    [openManageOrdersPanel, asset, closePanel, bottomPanelState],
  );

  const lastPriceDirection =
    marketData.priceHistory.length > 1
      ? marketData.priceHistory[marketData.priceHistory.length - 1]?.price >
        marketData.priceHistory[marketData.priceHistory.length - 2]?.price
        ? 'up'
        : 'down'
      : 'up';

  return (
    <div
      className="bg-brand-dark min-h-full w-full flex flex-col"
      style={{
        overscrollBehavior: 'none',
        ...({
          WebkitOverscrollBehavior: 'none',
        } as any),
      }}
    >
      <ScanlineOverlay priceDirection={lastPriceDirection} />

      {/* Header with gradient background */}
      <div className="relative z-10 flex-shrink-0">
        <div className="bg-gradient-to-b from-black/80 via-black/40 via-[70%] to-black/0">
          <Header asset={asset} onNavigate={onNavigate} />

          {/* Price + Countdown row */}
          <div className="flex border-b border-divider relative z-20">
            {/* Current Price Section */}
            <div className="flex-1 border-r border-divider px-3 py-2 flex flex-col">
              <div className="text-[#808080] text-[11px] tracking-[0.08em] -mb-1">
                CURRENT PRICE
              </div>
              <div
                className={`-mb-1 font-['Inter:Black',_sans-serif] text-nowrap text-[24px] sm:text-[28px] md:text-[32px] leading-tight ${
                  lastPriceDirection === 'up'
                    ? 'text-price-up'
                    : 'text-price-down'
                }`}
              >
                ${formatPrice(marketData.spotPrice)}
              </div>
            </div>

            {/* Expires In Section */}
            <div className="flex-1 px-3 py-2 flex flex-col items-end">
              <div className="text-[#808080] text-[11px] tracking-[0.08em] -mb-1">
                EXPIRES IN
              </div>
              <div className="-mb-1 font-['Inter:Black',_sans-serif] text-white text-nowrap text-[24px] sm:text-[28px] md:text-[32px] leading-tight">
                {formatTime()}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Chart container */}
      <motion.div
        layout
        transition={{ type: 'spring', damping: 30, stiffness: 300 }}
        className="flex-1 relative overflow-hidden"
      >
        <VisxChart
          spotPrice={marketData.spotPrice}
          priceDirection={lastPriceDirection}
          strikePrice={
            marketData.selectedStrikePrice > 0
              ? marketData.selectedStrikePrice
              : undefined
          }
          strikePrices={marketData.strikePrices}
          asset={asset}
          priceHistory={marketData.priceHistory}
          isLoading={marketData.isLoading || isRecovering}
          isReady={marketData.isReady}
          onTradeZoneSelect={handleTradeZoneSelect}
          onTradeZoneDeselect={handleTradeZoneDeselect}
          selectedTradeType={bottomPanelState.tradeData?.tradeType ?? null}
          selectedPositionGroup={
            bottomPanelState.closePositionData?.positionGroup ?? null
          }
          onPositionSelect={handlePositionSelect}
          selectedOrder={selectedOrder}
          onOrderSelect={handleOrderSelect}
          onManageOrders={handleManageOrders}
          isManageOrdersPanelOpen={
            bottomPanelState.activePanel === 'manageOrders' &&
            bottomPanelState.manageOrdersData?.strikePrice ===
              marketData.selectedStrikePrice
          }
          onStrikePriceSelect={price => {
            // Only close position panels when selecting strikes, keep trade panels open
            if (bottomPanelState.activePanel === 'closePosition') {
              closePanel();
            }

            // Clear selected order when selecting strikes
            if (selectedOrder) {
              setSelectedOrder(null);
            }

            // Handle deselection
            if (price === 0) {
              marketData.deselectStrike();
              // Close trade panel or manage orders panel when deselecting strike
              if (
                bottomPanelState.activePanel === 'trade' ||
                bottomPanelState.activePanel === 'manageOrders'
              ) {
                closePanel();
              }
              return;
            }

            // Select the strike (automatically syncs index and price)
            marketData.selectStrike(price);

            // If trade panel is open, update it with new strike
            if (
              bottomPanelState.activePanel === 'trade' &&
              bottomPanelState.tradeData
            ) {
              openTradePanel({
                tradeType: bottomPanelState.tradeData.tradeType,
                strikePrice: price,
                spotPrice: marketData.spotPrice,
                asset,
              });
            }
          }}
        />
      </motion.div>

      {/* Trade Panel */}
      <AnimatePresence>
        {bottomPanelState.activePanel === 'trade' &&
          bottomPanelState.tradeData && (
            <TradePanel
              isVisible={true}
              tradeType={bottomPanelState.tradeData.tradeType}
              strikePrice={bottomPanelState.tradeData.strikePrice}
              spotPrice={marketData.spotPrice}
              asset={bottomPanelState.tradeData.asset}
              onClose={closePanel}
              onTrade={(amount, orderType, limitPrice, _premium) => {
                const tradeData = bottomPanelState.tradeData;
                if (!tradeData) {
                  return;
                }

                // Validate limit orders have a valid limit price
                if (
                  orderType === 'LIMIT' &&
                  (limitPrice === undefined ||
                    limitPrice === null ||
                    limitPrice <= 0)
                ) {
                  console.warn(
                    '[MarketScreen] Invalid limit price for limit order:',
                    limitPrice,
                  );
                  return;
                }

                try {
                  // Create order through OrderService instead of direct buyOption
                  const order = orderService.createOrder({
                    asset: tradeData.asset,
                    type: tradeData.tradeType,
                    strikePrice: tradeData.strikePrice,
                    quantity: amount,
                    orderType: orderType,
                    limitPrice: orderType === 'LIMIT' ? limitPrice : undefined,
                    side: 'BUY',
                    expiryTimestamp: RoundService.getCurrentRoundExpiry(),
                  });

                  if (order) {
                    // Order created successfully - notification handled by useOrderNotifications
                    // Deselect the strike after successful order creation
                    marketData.deselectStrike();
                  } else {
                    // TODO: Show error message to user
                  }
                } catch {
                  // TODO: Show error message to user
                }

                closePanel();
              }}
            />
          )}
      </AnimatePresence>

      {/* Close Position Panel */}
      <AnimatePresence>
        {bottomPanelState.activePanel === 'closePosition' &&
          bottomPanelState.closePositionData && (
            <ClosePositionPanel
              isVisible={true}
              onClose={closePanel}
              positionGroup={bottomPanelState.closePositionData.positionGroup}
            />
          )}
      </AnimatePresence>

      {/* Manage Orders Panel */}
      <AnimatePresence>
        {bottomPanelState.activePanel === 'manageOrders' &&
          bottomPanelState.manageOrdersData && (
            <ManageOrdersPanel
              isVisible={true}
              orders={bottomPanelState.manageOrdersData.orders}
              strikePrice={bottomPanelState.manageOrdersData.strikePrice}
              asset={bottomPanelState.manageOrdersData.asset}
              onClose={closePanel}
            />
          )}
      </AnimatePresence>
    </div>
  );
}
